import React from 'react';
import { Box, Fade, IconButton, Snackbar, useMediaQuery, useTheme } from '@material-ui/core';
import { FieldArray, FieldArrayRenderProps, useFormikContext } from 'formik';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { SDCredentialValues } from '../../types/types';
import { LABEL_WIDTH, GAP, rowStyle } from '../../constants';
import { buildCredentialJson } from './utils/buildCredentialJson';
import PillLabel from '../../../ui/PillLable';
import EditablePillLabel from '../../../ui/EditPillLable';
import FieldInput from '../../../ui/FeildInput';
import { DeleteBtn, LinkOpenBtn, DIDDocBtn } from '../../../ui/ActionBtns';
import { AddButton } from '../../../ui/ActionBtns';
import SectionDivider from '../../../ui/SectionDivider';
import { WizardButton } from '../../../ui/WizardButton';
import BuildIcon from '@material-ui/icons/Build';
import JsonViewer from '../../../ui/JsonViwer';
import useClipboard from '../../../hooks/useClipboard';

interface SDCredentialFormContentProps {
    onSubmit: (value?: any) => void;
    onGenerate?: () => void;
    initialJson?: object;
}

const SDCredentialFormContent: React.FC<SDCredentialFormContentProps> = ({
    onSubmit,
    onGenerate,
    initialJson
}) => {
    const { values, setFieldValue } = useFormikContext<SDCredentialValues>();
    const [savedJson, setSavedJson] = React.useState<object | null>(
        initialJson && Object.keys(initialJson).length > 0 ? initialJson : {}
    );

    const { snackBarState, copyToClipboard, handleClose } = useClipboard();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

    const responsiveRow: React.CSSProperties = isMobile
        ? {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 6,
              marginBottom: 12
          }
        : rowStyle;

    const pillStyle: React.CSSProperties = isMobile ? { width: '100%' } : {};

    const inputRowStyle: React.CSSProperties = {
        display: 'flex',
        gap: GAP - 4,
        alignItems: 'center',
        flex: isMobile ? undefined : 1
    };

    return (
        <Box>
            {/* @context */}
            <FieldArray name="contexts">
                {({ push, remove }: FieldArrayRenderProps) => (
                    <Box>
                        {values.contexts.map((ctx, i) => (
                            <Box key={i} style={responsiveRow}>
                                {(!isMobile || i === 0) && (
                                    <Box
                                        style={{
                                            width: isMobile ? '100%' : LABEL_WIDTH,
                                            flexShrink: 0
                                        }}>
                                        {i === 0 && (
                                            <PillLabel style={{ width: '100%' }}>
                                                @context
                                            </PillLabel>
                                        )}
                                    </Box>
                                )}
                                <Box style={inputRowStyle}>
                                    <FieldInput
                                        value={ctx}
                                        placeholder="https://..."
                                        onChange={(v) => setFieldValue(`contexts[${i}]`, v)}
                                    />
                                    <LinkOpenBtn url={ctx} />
                                    <DeleteBtn
                                        onClick={() => remove(i)}
                                        disabled={values.contexts.length === 1}
                                    />
                                </Box>
                            </Box>
                        ))}
                        <Box
                            style={{
                                paddingLeft: isMobile ? 0 : LABEL_WIDTH + GAP,
                                marginBottom: 4
                            }}>
                            <AddButton label="Add context" onClick={() => push('')} />
                        </Box>
                    </Box>
                )}
            </FieldArray>

            {/* Core Fields */}
            <SectionDivider label="Core Fields" />
            <FieldArray name="coreFields">
                {({ push, remove }: FieldArrayRenderProps) => (
                    <Box>
                        {values.coreFields.map((field, i) => (
                            <Box key={i} style={responsiveRow}>
                                <EditablePillLabel
                                    value={field.key}
                                    placeholder="field name"
                                    onChange={(v) => setFieldValue(`coreFields[${i}].key`, v)}
                                    style={pillStyle}
                                />
                                <Box style={inputRowStyle}>
                                    <FieldInput
                                        value={field.value}
                                        placeholder="value"
                                        onChange={(v) => setFieldValue(`coreFields[${i}].value`, v)}
                                    />
                                    {field.value.startsWith('did:') && (
                                        <DIDDocBtn did={field.value} />
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </FieldArray>

            {/* Credential Subject */}
            <SectionDivider label="Credential Subject" />
            <Box
                style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: 'flex-start'
                }}>
                {/* credentialSubject label — left column on desktop, top row on mobile */}
                <Box
                    style={
                        isMobile
                            ? {
                                  width: '100%',
                                  paddingBottom: 8,
                                  marginBottom: 12,
                                  borderBottom: '1px solid #e0e3e8'
                              }
                            : {
                                  width: LABEL_WIDTH + 16,
                                  flexShrink: 0,
                                  borderRight: '1px solid #e0e3e8',
                                  paddingRight: 16,
                                  paddingTop: 4,
                                  alignSelf: 'stretch'
                              }
                    }>
                    <PillLabel style={{ width: '100%' }}>credentialSubject</PillLabel>
                </Box>

                <Box
                    style={{
                        flex: 1,
                        paddingLeft: isMobile ? 0 : 16,
                        width: isMobile ? '100%' : undefined
                    }}>
                    {/* holder */}
                    <Box style={responsiveRow}>
                        <PillLabel style={pillStyle}>holder</PillLabel>
                        <Box style={inputRowStyle}>
                            <FieldInput
                                value={values.holder}
                                placeholder="did:key:..."
                                onChange={(v) => setFieldValue('holder', v)}
                            />
                            {values.holder.startsWith('did:') && (
                                <DIDDocBtn did={values.holder} />
                            )}
                        </Box>
                    </Box>

                    {/* type */}
                    <Box
                        style={{
                            ...responsiveRow,
                            paddingBottom: 14,
                            borderBottom: '1px solid #e8eaed'
                        }}>
                        <PillLabel style={pillStyle}>type</PillLabel>
                        <Box style={inputRowStyle}>
                            <FieldInput
                                value={values.subjectType}
                                placeholder="type"
                                onChange={(v) => setFieldValue('subjectType', v)}
                            />
                            <DeleteBtn onClick={() => setFieldValue('subjectType', '')} />
                        </Box>
                    </Box>

                    {/* Custom Attributes */}
                    <FieldArray name="customAttributes">
                        {({ push, remove }: FieldArrayRenderProps) => (
                            <Box style={{ marginTop: 12 }}>
                                {values.customAttributes.map((attr, i) => (
                                    <Box
                                        key={i}
                                        style={{
                                            display: 'flex',
                                            flexDirection: isMobile ? 'column' : 'row',
                                            alignItems: 'flex-start',
                                            gap: GAP,
                                            marginBottom: 12
                                        }}>
                                        <Box
                                            style={{
                                                width: isMobile ? '100%' : LABEL_WIDTH,
                                                flexShrink: 0
                                            }}>
                                            <PillLabel style={{ width: '100%' }}>
                                                customAttribute
                                            </PillLabel>
                                        </Box>
                                        <Box
                                            style={{
                                                flex: 1,
                                                width: isMobile ? '100%' : undefined,
                                                borderLeft: isMobile ? 'none' : '3px solid #1eb89a',
                                                borderTop: isMobile ? '3px solid #1eb89a' : 'none',
                                                paddingLeft: isMobile ? 0 : 16,
                                                paddingTop: isMobile ? 8 : 0
                                            }}>
                                            <Box style={{ marginBottom: 8 }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => remove(i)}
                                                    style={{ color: '#c8cdd6', padding: 4 }}>
                                                    <DeleteOutlineIcon style={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Box>
                                            {(['type', 'name', 'value'] as const).map((field) => (
                                                <Box
                                                    key={field}
                                                    style={{ ...responsiveRow, marginBottom: 8 }}>
                                                    <PillLabel
                                                        style={{ width: isMobile ? '100%' : 120 }}>
                                                        {field}
                                                    </PillLabel>
                                                    <FieldInput
                                                        value={attr[field]}
                                                        placeholder={field}
                                                        onChange={(v) =>
                                                            setFieldValue(
                                                                `customAttributes[${i}].${field}`,
                                                                v
                                                            )
                                                        }
                                                    />
                                                </Box>
                                            ))}
                                        </Box>
                                        {!isMobile && <Box style={{ width: 30, flexShrink: 0 }} />}
                                    </Box>
                                ))}
                                <Box style={{ marginBottom: 16 }}>
                                    <AddButton
                                        label="Add custom attribute"
                                        onClick={() =>
                                            push({ type: 'NameValuePair', name: '', value: '' })
                                        }
                                    />
                                </Box>
                            </Box>
                        )}
                    </FieldArray>

                    {/* Subject Attrs */}
                    <FieldArray name="subjectAttrs">
                        {({ push, remove }: FieldArrayRenderProps) => (
                            <Box style={{ borderTop: '1px solid #e8eaed', paddingTop: 14 }}>
                                {values.subjectAttrs.map((attr, i) => (
                                    <Box key={i} style={responsiveRow}>
                                        <EditablePillLabel
                                            value={attr.key}
                                            placeholder="attribute name"
                                            onChange={(v) =>
                                                setFieldValue(`subjectAttrs[${i}].key`, v)
                                            }
                                            style={pillStyle}
                                        />
                                        <Box style={inputRowStyle}>
                                            <FieldInput
                                                value={attr.value}
                                                placeholder="value"
                                                onChange={(v) =>
                                                    setFieldValue(`subjectAttrs[${i}].value`, v)
                                                }
                                            />
                                            <DeleteBtn onClick={() => remove(i)} />
                                        </Box>
                                    </Box>
                                ))}
                                <AddButton
                                    label="Add attribute"
                                    onClick={() => push({ key: 'newAttr', value: '' })}
                                />
                            </Box>
                        )}
                    </FieldArray>
                </Box>
            </Box>

            <Box style={{ display: 'flex' }}>
                <WizardButton
                    variant="primary"
                    onClick={() => {
                        const json = buildCredentialJson(values);
                        setSavedJson(json);
                        onSubmit(json);
                        onGenerate?.();
                    }}
                    style={{ marginTop: '10px', color: '#ffffff' }}
                    endIcon={<BuildIcon style={{ fontSize: '1.1rem' }} />}
                    fullWidth>
                    Next: Generate Object
                </WizardButton>
            </Box>

            {savedJson && Object.keys(savedJson).length > 0 && (
                <Box style={{ marginTop: 24 }}>
                    <JsonViewer content={savedJson} onCopy={copyToClipboard} />
                </Box>
            )}
            <Snackbar
                open={snackBarState.open}
                onClose={handleClose}
                TransitionComponent={Fade}
                message={snackBarState.text}
                autoHideDuration={5000}
            />
        </Box>
    );
};

export default SDCredentialFormContent;
